import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BookOpen, Download, Star, ChevronRight, ArrowLeft, Clock, FileText, Award, BarChart2, MessageSquare, Tag } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { getReadingHistory } from "../utils/gamification";
import CommentSection from "../components/CommentSection";
import BookCard from "../components/BookCard";

const getQuote = () => ({
  text: "The books we read help shape our minds, but the Scriptures shape our souls. Seek wisdom in all things.",
  cite: "Christian Classics Initiative",
});

/* ── tiny reusable components ────────────────────────────────── */
function SectionTitle({ children, icon: Icon }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "1.25rem" }}>
      <div style={{ width: "3px", height: "1.4rem", background: "linear-gradient(180deg,var(--gold) 0%,var(--gold-light) 100%)", borderRadius: "2px" }} />
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.45rem", fontWeight: 700, color: "var(--ink)", display: "flex", alignItems: "center", gap: "8px" }}>
        {Icon && <Icon size={16} color="var(--gold)" strokeWidth={2} />}
        {children}
      </h2>
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: "var(--card-bg)",
      border: "1px solid var(--card-border)",
      borderRadius: "var(--radius-lg)",
      padding: "1.5rem",
      boxShadow: "var(--shadow)",
      ...style,
    }}>{children}</div>
  );
}

function PrimaryBtn({ onClick, children, disabled, style }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width: "100%", padding: "14px 24px",
      background: disabled ? "var(--dust)" : "linear-gradient(135deg,var(--gold) 0%,var(--gold-2) 100%)",
      color: disabled ? "var(--muted)" : "#fff",
      border: "none", borderRadius: "var(--radius-full)",
      fontFamily: "var(--font-body)", fontSize: "0.95rem", fontWeight: 700,
      cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: disabled ? "none" : "var(--shadow-gold)",
      transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
      ...style,
    }}
      onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--shadow-gold-lg)"; } }}
      onMouseLeave={(e) => { if (!disabled) { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "var(--shadow-gold)"; } }}
    >{children}</button>
  );
}

function SecondaryBtn({ onClick, children, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width: "100%", padding: "12px 24px",
      background: "none", color: disabled ? "var(--muted)" : "var(--ink)",
      border: "1px solid var(--dust-2)", borderRadius: "var(--radius-full)",
      fontFamily: "var(--font-body)", fontSize: "0.88rem", fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
    }}
      onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.color = "var(--gold)"; } }}
      onMouseLeave={(e) => { if (!disabled) { e.currentTarget.style.borderColor = "var(--dust-2)"; e.currentTarget.style.color = "var(--ink)"; } }}
    >{children}</button>
  );
}

/* ── main component ──────────────────────────────────────────── */
export default function BookDetailPage({ books, loading, onDownload }) {
  const { bookId } = useParams();
  const navigate   = useNavigate();
  const [dlState,  setDlState]  = useState("idle");

  const book = useMemo(() => books.find((b) => b.id === bookId), [books, bookId]);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const orig = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => { window.history.scrollRestoration = orig; };
    }
  }, []);

  useEffect(() => {
    if (!loading && book) {
      window.scrollTo(0, 0);
      const t = setTimeout(() => window.scrollTo(0, 0), 50);
      return () => clearTimeout(t);
    }
  }, [loading, bookId, book]);

  const [userRating, setUserRating] = useState(() => {
    const s = localStorage.getItem(`user_rating_${bookId}`);
    return s ? Number(s) : 0;
  });
  const [ratingHover, setRatingHover] = useState(0);
  const [ratedMsg,    setRatedMsg]    = useState("");

  useEffect(() => {
    const s = localStorage.getItem(`user_rating_${bookId}`);
    setUserRating(s ? Number(s) : 0);
    setDlState("idle");
  }, [bookId]);

  const handleRate = (stars) => {
    setUserRating(stars);
    localStorage.setItem(`user_rating_${bookId}`, String(stars));
    setRatedMsg("Rating saved — thank you!");
    setTimeout(() => setRatedMsg(""), 3500);
  };

  const progressInfo = useMemo(() => {
    if (!book) return { percent: 0, pagesRead: 0, savedPage: null };
    const uniquePagesSet = new Set();
    const history = getReadingHistory();
    Object.values(history).forEach((day) => {
      if (day?.pages?.[book.id]) day.pages[book.id].forEach((p) => uniquePagesSet.add(p));
    });
    const pagesRead  = uniquePagesSet.size;
    const totalPages = book.pages || 100;
    const percent    = Math.min(100, Math.round((pagesRead / totalPages) * 100));
    const saved      = localStorage.getItem(`bookmark_${book.id}`);
    return { percent, pagesRead, savedPage: saved ? Number(saved) : null };
  }, [book]);

  const ratingStats = useMemo(() => {
    if (!book) return null;
    const base   = Math.round((book.downloadCount || 10) * 1.5) + 12;
    const rating = book.rating || 4.5;
    let five  = Math.round(base * (rating >= 4.8 ? 0.82 : rating >= 4.5 ? 0.68 : 0.45));
    let four  = Math.round(base * (rating >= 4.8 ? 0.12 : rating >= 4.5 ? 0.22 : 0.35));
    let three = Math.round(base * 0.04);
    let two   = Math.round(base * 0.02);
    let one   = Math.max(0, base - (five + four + three + two));
    if (userRating === 5) five++; else if (userRating === 4) four++;
    else if (userRating === 3) three++; else if (userRating === 2) two++; else if (userRating === 1) one++;
    const total = five + four + three + two + one;
    const avg   = total > 0 ? Number(((five*5+four*4+three*3+two*2+one)/total).toFixed(1)) : rating;
    return {
      average: avg, total,
      stars: [five, four, three, two, one],
      percentages: [five, four, three, two, one].map((n) => Math.round((n / total) * 100)),
    };
  }, [book, userRating]);

  const schema = useMemo(() => {
    if (!book) return null;
    return {
      "@context": "https://schema.org", "@type": "Book",
      "name": book.title || "Untitled",
      "author": { "@type": "Person", "name": book.author || "Unknown" },
      "genre": book.genre, "numberOfPages": book.pages,
      "datePublished": book.year !== undefined ? String(book.year) : undefined,
      "description": book.description || "",
      "offers": { "@type": "Offer", "price": "0.00", "priceCurrency": "INR", "availability": "https://schema.org/InStock" },
      ...(book.rating ? { "aggregateRating": { "@type": "AggregateRating", "ratingValue": book.rating, "bestRating": "5", "worstRating": "1", "ratingCount": "10" } } : {}),
    };
  }, [book]);

  useSEO({
    title: book ? `${book.title} by ${book.author} — Ebenezer Books` : loading ? "Loading…" : "Not Found",
    description: book ? `${(book.description || "").slice(0, 150)}… Read ${book.title} by ${book.author} online.` : "",
    keywords: book ? `${book.title}, ${book.author}, ${book.genre}, christian classics` : "",
    ogType: "book", schema,
  });

  const related = useMemo(() => {
    if (!book) return [];
    return books.filter((b) => b.genre === book.genre && b.id !== book.id).slice(0, 4);
  }, [books, book]);

  async function handleDownload(format = "pdf") {
    if (dlState !== "idle") return;
    setDlState("loading");
    await onDownload(book, format);
    setDlState("done");
    setTimeout(() => setDlState("idle"), 3000);
  }

  /* ── loading skeleton ── */
  if (loading) {
    return (
      <div style={{ maxWidth: "var(--max-w)", margin: "0 auto", padding: "3rem 2rem" }}>
        <div style={{ display: "flex", gap: "3rem", flexWrap: "wrap" }}>
          <div className="skeleton" style={{ width: "300px", height: "400px", borderRadius: "var(--radius-xl)", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: "260px", display: "flex", flexDirection: "column", gap: "16px" }}>
            {[180, 300, 220, 100, 60].map((w, i) => (
              <div key={i} className="skeleton" style={{ width: `${w}px`, maxWidth: "100%", height: i === 0 ? "36px" : "16px", borderRadius: "6px" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── not found ── */
  if (!book) {
    return (
      <div style={{ textAlign: "center", padding: "8rem 2rem" }}>
        <div style={{ fontSize: "4rem", marginBottom: "1rem", opacity: 0.3 }}>📚</div>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: "2rem", color: "var(--ink)", marginBottom: "0.5rem" }}>Book Not Found</h2>
        <p style={{ color: "var(--muted)", marginBottom: "2rem" }}>The book you're looking for doesn't exist in our library.</p>
        <button onClick={() => navigate("/")} style={{ padding: "10px 28px", background: "var(--gold)", color: "#fff", border: "none", borderRadius: "var(--radius-full)", fontWeight: 700, cursor: "pointer" }}>
          ← Back to Library
        </button>
      </div>
    );
  }

  const yearLabel  = book.year < 0 ? `${Math.abs(book.year)} BC` : book.year;
  const coverFrom  = book.coverColor?.[0] || book.colorFrom || "#1a1208";
  const coverTo    = book.coverColor?.[1] || book.colorTo   || "#8b4513";
  const bookQuote  = getQuote();

  const detailRows = [
    { icon: "✍️",  label: "Author",        value: book.author },
    { icon: "📂",  label: "Genre",         value: book.genre },
    { icon: "📅",  label: "Published",     value: yearLabel },
    { icon: "📄",  label: "Pages",         value: `${book.pages} pages` },
    { icon: "⭐",  label: "Rating",        value: `${ratingStats?.average || book.rating} / 5.0` },
    { icon: "📥",  label: "Downloads",     value: `${book.downloadCount || 0} times` },
    { icon: "💰",  label: "Price",         value: "Free — Public Domain" },
    { icon: "📖",  label: "Format",        value: book.epubPath && book.pdfPath ? "EPUB + PDF" : book.epubPath ? "EPUB" : "PDF" },
  ];

  return (
    <>
      {/* ── Injected CSS ── */}
      <style>{`
        .book-3d { perspective: 1200px; }
        .book-3d-inner {
          width: 240px; height: 320px; position: relative;
          transform-style: preserve-3d;
          transform: rotateY(-12deg) rotateX(4deg);
          transition: transform 0.5s cubic-bezier(0.16,1,0.3,1), box-shadow 0.5s;
          box-shadow: -12px 14px 30px rgba(0,0,0,0.25);
          border-radius: 4px 12px 12px 4px;
        }
        .book-3d-inner:hover {
          transform: rotateY(-28deg) rotateX(8deg) translateY(-8px);
          box-shadow: -28px 28px 50px rgba(0,0,0,0.32);
        }
        .book-front {
          position: absolute; inset: 0;
          transform: translateZ(10px);
          border-radius: 4px 12px 12px 4px;
          overflow: hidden;
          display: flex; flex-direction: column;
          align-items: center; justify-content: space-between;
          padding: 2rem 1.5rem;
          box-shadow: inset -4px 0 6px rgba(0,0,0,0.25);
        }
        .book-spine {
          position: absolute; left: 0; top: 0; width: 16px; height: 100%;
          transform-origin: left center;
          transform: rotateY(-90deg) translateZ(10px);
          border-radius: 4px 0 0 4px;
          background: linear-gradient(to right, rgba(0,0,0,0.45), rgba(0,0,0,0) 40%, rgba(255,255,255,0.06) 70%, rgba(0,0,0,0.4));
          z-index: 2;
        }
        .book-pages {
          position: absolute; right: 0; top: 3px;
          width: 16px; height: calc(100% - 6px);
          transform-origin: right center;
          transform: rotateY(90deg) translateZ(10px);
          background: repeating-linear-gradient(90deg, var(--dust) 0, var(--dust) 2px, var(--cream) 2px, var(--cream) 4px);
          box-shadow: inset 2px 0 5px rgba(0,0,0,0.15);
        }
        .book-back {
          position: absolute; inset: 0;
          transform: translateZ(-10px) rotateY(180deg);
          border-radius: 12px 4px 4px 12px;
        }
        .book-glow {
          position: absolute; inset: 0;
          background: linear-gradient(135deg,rgba(255,255,255,0.16) 0%,transparent 45%,rgba(0,0,0,0.28) 100%);
          pointer-events: none; z-index: 2;
        }
        .rating-star { background:none; border:none; cursor:pointer; font-size:1.7rem; padding:0 3px; transition:transform 0.15s; }
        .rating-star:hover { transform: scale(1.25); }
        .progress-bar-track { height:6px; background:var(--dust); border-radius:3px; overflow:hidden; }
        .progress-bar-fill  { height:100%; background:linear-gradient(90deg,var(--gold),var(--sage)); border-radius:3px; transition:width 0.5s ease; }
        @media(max-width:900px){ .detail-grid { flex-direction:column !important; } .book-sticky { position:relative !important; top:0 !important; } }
      `}</style>

      {/* ── Breadcrumb ── */}
      <div style={{ maxWidth: "var(--max-w)", margin: "0 auto", padding: "1.25rem 2rem 0", display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8rem", color: "var(--muted)" }}>
        <span onClick={() => navigate("/")} style={{ cursor: "pointer", color: "var(--gold)", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
          <BookOpen size={13} /> Library
        </span>
        <ChevronRight size={12} style={{ opacity: 0.4 }} />
        <span onClick={() => navigate(`/search?q=${encodeURIComponent(book.genre)}`)} style={{ cursor: "pointer", color: "var(--gold)", fontWeight: 500 }}>{book.genre}</span>
        <ChevronRight size={12} style={{ opacity: 0.4 }} />
        <span style={{ color: "var(--ink)", maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{book.title}</span>
      </div>

      {/* ── Main layout ── */}
      <div className="detail-grid" style={{ maxWidth: "var(--max-w)", margin: "0 auto", padding: "2rem 2rem 4rem", display: "flex", gap: "3rem", alignItems: "flex-start" }}>

        {/* ═══ LEFT — 3D Book + Stats ═══ */}
        <div className="book-sticky" style={{ width: "340px", maxWidth: "100%", flexShrink: 0, position: "sticky", top: "calc(var(--nav-h) + 1.5rem)", display: "flex", flexDirection: "column", gap: "1.25rem", alignItems: "center" }}>

          {/* 3D Book */}
          <div className="book-3d" style={{ display: "flex", justifyContent: "center", padding: "2rem 0 1rem" }}>
            <div className="book-3d-inner">
              <div className="book-spine" style={{ backgroundColor: coverFrom }} />
              <div className="book-front" style={{ background: `linear-gradient(155deg,${coverFrom},${coverTo})` }}>
                <div className="book-glow" />
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", height: "100%", width: "100%", textAlign: "center", position: "relative", zIndex: 1 }}>
                  {book.badge
                    ? <div style={{ background: "var(--gold)", color: "#fff", fontSize: "0.6rem", fontWeight: 800, padding: "4px 12px", borderRadius: "var(--radius-full)", alignSelf: "flex-end", letterSpacing: "0.08em", textTransform: "uppercase" }}>{book.badge}</div>
                    : <div />
                  }
                  <div style={{ fontSize: "3.5rem", filter: "drop-shadow(0 4px 14px rgba(0,0,0,0.4))" }}>📖</div>
                  <div>
                    <div style={{ fontFamily: "var(--font-display)", color: "#fff", fontSize: "1.35rem", fontWeight: 700, lineHeight: 1.3, textShadow: "0 2px 10px rgba(0,0,0,0.4)", marginBottom: "0.35rem" }}>{book.title}</div>
                    <div style={{ color: "rgba(255,255,255,0.72)", fontSize: "0.85rem" }}>{book.author}</div>
                  </div>
                </div>
              </div>
              <div className="book-pages" />
              <div className="book-back" style={{ background: coverTo }} />
            </div>
          </div>

          {/* Quick stats */}
          <Card style={{ width: "100%", padding: "0" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
              {[
                { label: "Pages", value: book.pages },
                { label: "Rating", value: `★ ${ratingStats?.average || book.rating}`, color: "var(--gold)" },
                { label: "Era", value: yearLabel },
              ].map(({ label, value, color }, i) => (
                <div key={label} style={{ padding: "1rem 0.75rem", textAlign: "center", borderRight: i < 2 ? "1px solid var(--card-border)" : "none" }}>
                  <div style={{ fontSize: "0.65rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>{label}</div>
                  <div style={{ fontSize: "1.05rem", fontWeight: 700, color: color || "var(--ink)" }}>{value}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Badges */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "var(--sage-faint)", color: "var(--sage)", fontSize: "0.72rem", fontWeight: 700, padding: "5px 12px", borderRadius: "var(--radius-full)", border: "1px solid rgba(58,102,69,0.2)" }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "var(--sage)" }} />
              Open Library Edition
            </span>
            <span style={{ background: "var(--gold-faint)", color: "var(--gold)", fontSize: "0.72rem", fontWeight: 700, padding: "5px 12px", borderRadius: "var(--radius-full)", border: "1px solid var(--gold-faint-2)" }}>
              Public Domain
            </span>
          </div>

          <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: "0.8rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px", transition: "color 0.15s" }}
            onMouseEnter={(e) => e.currentTarget.style.color = "var(--ink)"}
            onMouseLeave={(e) => e.currentTarget.style.color = "var(--muted)"}
          >
            <ArrowLeft size={13} /> Back to Catalog
          </button>
        </div>

        {/* ═══ RIGHT — Details & Actions ═══ */}
        <div style={{ flex: 1, minWidth: "0", display: "flex", flexDirection: "column", gap: "2rem" }}>

          {/* ── Title block ── */}
          <div>
            <span style={{ display: "inline-block", background: "var(--gold-faint)", color: "var(--gold)", fontSize: "0.7rem", fontWeight: 700, padding: "4px 14px", borderRadius: "var(--radius-full)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "0.85rem", border: "1px solid var(--gold-faint-2)" }}>
              {book.genre}
            </span>
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2rem,3.5vw,2.8rem)", fontWeight: 600, color: "var(--ink)", lineHeight: 1.15, marginBottom: "0.5rem" }}>
              {book.title}
            </h1>
            <p style={{ fontSize: "1.05rem", color: "var(--muted)" }}>
              by <span style={{ color: "var(--ink)", fontWeight: 600 }}>{book.author}</span>
            </p>
          </div>

          {/* ── CTA panel ── */}
          <Card>
            {/* Reading progress */}
            {progressInfo.pagesRead > 0 && (
              <div style={{ marginBottom: "1.25rem", padding: "1rem 1.25rem", background: "var(--sage-faint)", borderRadius: "var(--radius)", border: "1px solid rgba(58,102,69,0.15)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", fontWeight: 700, color: "var(--muted)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "6px" }}>
                  <span>Reading Progress</span>
                  <span style={{ color: "var(--sage)" }}>{progressInfo.percent}%</span>
                </div>
                <div className="progress-bar-track">
                  <div className="progress-bar-fill" style={{ width: `${progressInfo.percent}%` }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "var(--muted)", marginTop: "6px" }}>
                  <span>{progressInfo.savedPage ? `Page ${progressInfo.savedPage}` : `${progressInfo.pagesRead} pages read`}</span>
                  <span>of {book.pages} pages</span>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {book.epubPath && book.pdfPath ? (
                <>
                  <PrimaryBtn onClick={() => navigate(`/book/${book.id}/read?format=epub`)}>
                    <BookOpen size={16} strokeWidth={2.5} />
                    {progressInfo.savedPage ? `Resume EPUB — Page ${progressInfo.savedPage}` : "Read EPUB (Reflowable)"}
                  </PrimaryBtn>
                  <SecondaryBtn onClick={() => navigate(`/book/${book.id}/read?format=pdf`)}>
                    <FileText size={15} /> Read PDF (Original Layout)
                  </SecondaryBtn>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    {["epub", "pdf"].map((fmt) => (
                      <button key={fmt} onClick={() => handleDownload(fmt)} disabled={dlState === "loading"} style={{
                        padding: "9px 14px", border: `1px solid ${dlState === "done" ? "var(--sage)" : "var(--dust-2)"}`,
                        borderRadius: "var(--radius-full)", background: dlState === "done" ? "var(--sage-faint)" : "none",
                        color: dlState === "done" ? "var(--sage)" : dlState === "loading" ? "var(--muted)" : "var(--ink)",
                        fontSize: "0.8rem", fontWeight: 600, cursor: dlState === "loading" ? "wait" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", transition: "all 0.15s",
                      }}>
                        <Download size={13} /> {fmt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </>
              ) : book.epubPath ? (
                <>
                  <PrimaryBtn onClick={() => navigate(`/book/${book.id}/read?format=epub`)}>
                    <BookOpen size={16} strokeWidth={2.5} />
                    {progressInfo.savedPage ? `Resume Reading — Page ${progressInfo.savedPage}` : "Read Online (EPUB)"}
                  </PrimaryBtn>
                  <SecondaryBtn onClick={() => handleDownload("epub")} disabled={dlState === "loading"}>
                    <Download size={15} /> {dlState === "done" ? "Downloaded!" : dlState === "loading" ? "Downloading…" : "Download EPUB — Free"}
                  </SecondaryBtn>
                </>
              ) : (
                <>
                  <PrimaryBtn onClick={() => navigate(`/book/${book.id}/read?format=pdf`)}>
                    <BookOpen size={16} strokeWidth={2.5} />
                    {progressInfo.savedPage ? `Resume Reading — Page ${progressInfo.savedPage}` : "Read Online (PDF)"}
                  </PrimaryBtn>
                  <SecondaryBtn onClick={() => handleDownload("pdf")} disabled={dlState === "loading"}>
                    <Download size={15} /> {dlState === "done" ? "Downloaded!" : dlState === "loading" ? "Downloading…" : "Download PDF — Free"}
                  </SecondaryBtn>
                </>
              )}
            </div>
          </Card>

          {/* ── About ── */}
          <section>
            <SectionTitle icon={BookOpen}>About this Book</SectionTitle>
            <p style={{ fontSize: "1rem", lineHeight: 1.85, color: "var(--ink)", marginBottom: "1.5rem", textAlign: "justify" }}>
              {book.description}
            </p>
            {/* Quote */}
            <div style={{ borderLeft: "3px solid var(--gold)", paddingLeft: "1.25rem", margin: "0 0 1.5rem", background: "var(--gold-faint)", padding: "1.25rem 1.5rem", borderRadius: "0 var(--radius) var(--radius) 0" }}>
              <p style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", fontStyle: "italic", lineHeight: 1.6, color: "var(--ink)", marginBottom: "0.5rem" }}>"{bookQuote.text}"</p>
              <cite style={{ fontSize: "0.78rem", color: "var(--muted)", fontStyle: "normal", fontWeight: 600 }}>— {bookQuote.cite}</cite>
            </div>
            {/* Tags */}
            {book.tags?.length > 0 && (
              <div>
                <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted)", marginBottom: "0.6rem", display: "flex", alignItems: "center", gap: "5px" }}>
                  <Tag size={11} /> Tags & Topics
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {book.tags.map((t) => (
                    <span key={t} style={{ background: "var(--cream-2)", border: "1px solid var(--dust-2)", borderRadius: "var(--radius-full)", padding: "5px 14px", fontSize: "0.78rem", color: "var(--muted)", cursor: "default", transition: "all 0.15s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--gold)"; e.currentTarget.style.color = "var(--ink)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--dust-2)"; e.currentTarget.style.color = "var(--muted)"; }}
                    >{t}</span>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* ── Book Details table ── */}
          <section>
            <SectionTitle icon={FileText}>Book Details</SectionTitle>
            <Card style={{ padding: 0, overflow: "hidden" }}>
              {detailRows.map(({ icon, label, value }, i) => (
                <div key={label} style={{ display: "flex", alignItems: "center", padding: "0.85rem 1.25rem", borderBottom: i < detailRows.length - 1 ? "1px solid var(--card-border)" : "none", background: i % 2 === 0 ? "rgba(0,0,0,0.015)" : "transparent", gap: "10px" }}>
                  <span style={{ fontSize: "1rem", flexShrink: 0 }}>{icon}</span>
                  <span style={{ width: "130px", fontSize: "0.82rem", color: "var(--muted)", fontWeight: 600, flexShrink: 0 }}>{label}</span>
                  <span style={{ fontSize: "0.88rem", color: "var(--ink)", fontWeight: 500 }}>{value}</span>
                </div>
              ))}
            </Card>
          </section>

          {/* ── Reviews ── */}
          <section>
            <SectionTitle icon={Star}>Community Reviews</SectionTitle>
            <Card>
              <div style={{ display: "flex", gap: "2.5rem", flexWrap: "wrap", alignItems: "center", marginBottom: "1.5rem" }}>
                {/* Score */}
                <div style={{ textAlign: "center", flexShrink: 0 }}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: "3.5rem", fontWeight: 700, color: "var(--ink)", lineHeight: 1 }}>{ratingStats?.average}</div>
                  <div style={{ display: "flex", justifyContent: "center", gap: "2px", margin: "6px 0" }}>
                    {[1,2,3,4,5].map((s) => (
                      <Star key={s} size={14} fill={s <= Math.round(ratingStats?.average || 0) ? "var(--gold)" : "none"} color={s <= Math.round(ratingStats?.average || 0) ? "var(--gold)" : "var(--dust-2)"} />
                    ))}
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>{ratingStats?.total} reviews</div>
                </div>
                {/* Bars */}
                <div style={{ flex: 1, minWidth: "180px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  {ratingStats?.percentages.map((pct, idx) => (
                    <div key={5 - idx} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.75rem" }}>
                      <span style={{ width: "36px", color: "var(--muted)", textAlign: "right", flexShrink: 0 }}>{5 - idx}★</span>
                      <div style={{ flex: 1, height: "7px", background: "var(--dust)", borderRadius: "4px", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,var(--gold),var(--gold-light))", borderRadius: "4px", transition: "width 0.8s cubic-bezier(0.16,1,0.3,1)" }} />
                      </div>
                      <span style={{ width: "30px", color: "var(--muted)", flexShrink: 0 }}>{pct}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rate */}
              <div style={{ borderTop: "1px solid var(--card-border)", paddingTop: "1.25rem", textAlign: "center" }}>
                <p style={{ fontSize: "0.85rem", color: "var(--muted)", marginBottom: "0.6rem" }}>
                  {userRating ? "Your rating has been saved!" : "Have you read this book? Rate it:"}
                </p>
                <div style={{ display: "flex", justifyContent: "center", gap: "4px" }}>
                  {[1,2,3,4,5].map((s) => (
                    <button key={s} className="rating-star"
                      onMouseEnter={() => !userRating && setRatingHover(s)}
                      onMouseLeave={() => !userRating && setRatingHover(0)}
                      onClick={() => handleRate(s)}
                      style={{ color: s <= (ratingHover || userRating) ? "var(--gold)" : "var(--dust-2)" }}
                    >★</button>
                  ))}
                </div>
                {ratedMsg && <p style={{ fontSize: "0.78rem", color: "var(--sage)", marginTop: "0.5rem", fontWeight: 500 }}>{ratedMsg}</p>}
              </div>
            </Card>
          </section>
        </div>
      </div>

      {/* ── Related Books ── */}
      {related.length > 0 && (
        <div style={{ background: "var(--cream-2)", borderTop: "1px solid var(--card-border)", borderBottom: "1px solid var(--card-border)", padding: "3rem 2rem" }}>
          <div style={{ maxWidth: "var(--max-w)", margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "1.5rem" }}>
              <div style={{ width: "3px", height: "1.4rem", background: "linear-gradient(180deg,var(--gold) 0%,var(--gold-light) 100%)", borderRadius: "2px" }} />
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: "1.45rem", fontWeight: 700, color: "var(--ink)" }}>
                You Might Also Like
              </h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(185px,1fr))", gap: "1.25rem" }}>
              {related.map((rb) => (
                <BookCard key={rb.id} book={rb} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Comments ── */}
      <CommentSection bookId={book.id} />
    </>
  );
}
